import { Box } from '@mui/material';
import React from 'react';
import { GlobalContentFooter } from '../GlobalContentFooter';
import { globalHeaderHeightNormal, globalHeaderHeightSmall } from '../GlobalHeader/constants';
import { globalInfoHeight } from '../GlobalInfo/constants';
import { globalMenuWidthFull, globalMenuWidthSmall } from '../GlobalMenu/constants';
import { globalPinnedBarWidth } from '../GlobalPinnedBar/constants';
import { globalTabsHeight } from '../GlobalTabsBar/constants';
import { themeColors } from '../JuvareTheme';
import { useHoverSupported, useSmallDevice, useSmallOrMediumDevice } from '../hooks';
/**
 * This is the main container for page content in the global layout.
 * It dynamically adjusts padding and layout based on device size, UI state (e.g., menu or info panel open),
 * and optional footer content. It supports tab-based routing, full-height content, and responsiveness.
 *
 * @param {GlobalContentProps} props - Props for configuring the layout and behavior of the global content container.
 * @param {boolean} props.menuOpen - Whether the left-side menu is currently open.
 * @param {boolean} props.infoOpen - Whether the top info banner is currently shown.
 * @param {boolean} props.showTabs - Whether tab navigation is displayed.
 * @param {boolean} props.pinnedBarOpen - Whether the pinned bar is expanded.
 * @param {boolean} [props.fullHeightWithoutScrollbars] - If true, disables scrollbars and forces 100% height.
 * @param {boolean} [props.hideHeader] - Whether to hide the global header.
 * @param {boolean} [props.hideMenu] - Whether to hide the global menu.
 * @param {string} [props.tabPath] - Optional tab path identifier used to isolate rendering for tab content.
 * @param {'icon-only' | 'icon-and-label'} [props.menuMode] - Menu mode, affects width and layout of the menu. Use only with UX approval.
 * @param {boolean} [props.drawerOpen] - Whether a drawer component is currently open (affects accessibility).
 * @param {number} [props.drawerWidth] - Width of the drawer, if applicable.
 * @param {boolean} [props.isDrawerFullScreen] - Whether the drawer occupies the full screen.
 * @param {SxProps<Theme>} [props.sx] - Additional system styles for the outer container.
 * @param {React.ReactNode} [props.footerCopyright] - Optional copyright/footer text.
 * @param {React.ReactNode[]} [props.footerLinks] - Optional list of footer links.
 * @param {React.ReactNode} props.children - Main page content to be rendered inside the layout.
 *
 * @returns {JSX.Element} A responsive layout container with optional header, tabs, menu padding, and footer.
 */
export const GlobalContent = (props) => {
    const smallDevice = useSmallDevice();
    const smallOrMediumDevice = useSmallOrMediumDevice();
    const hoverSupported = useHoverSupported() && !smallDevice;
    const headerHeight = !props.hideHeader ? (smallDevice ? globalHeaderHeightSmall : globalHeaderHeightNormal) : 0;
    return (React.createElement(Box, { id: props.tabPath ? undefined : 'main-content-wrapper', className: props.tabPath ? 'main-content-tab' : undefined, "data-tab-path": props.tabPath, sx: [
            {
                display: props.tabPath ? 'none' : 'block',
                boxSizing: 'border-box',
                height: '100%',
                paddingTop: headerHeight + (props.infoOpen ? globalInfoHeight : 0) + (props.showTabs ? globalTabsHeight : 0) + 'px',
                paddingLeft: props.hideMenu
                    ? 0
                    : (props.menuOpen && hoverSupported && props.menuMode !== 'icon-and-label' ? globalMenuWidthSmall : 0) +
                        (props.menuOpen && hoverSupported && props.menuMode === 'icon-and-label' ? globalMenuWidthFull : 0) +
                        (props.pinnedBarOpen && !smallOrMediumDevice ? globalPinnedBarWidth : 0) +
                        'px',
                overflow: 'hidden',
                transition: (theme) => theme.transitions.create('padding-left') + ',' + theme.transitions.create('margin-right', { duration: 400 }),
                '@media print': {
                    padding: 0,
                },
            },
            ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
        ], "aria-hidden": props.drawerOpen },
        React.createElement(Box, { id: 'main-content', tabIndex: -1, component: 'main', sx: {
                position: 'relative',
                height: '100%',
                overflow: props.fullHeightWithoutScrollbars ? 'hidden' : 'auto',
                backgroundColor: themeColors.background,
                outline: 'none',
                '@media print': {
                    backgroundColor: 'white',
                },
            } }, props.footerCopyright == null && (props.footerLinks == null || props.footerLinks.length === 0) ? (props.children) : (React.createElement(Box, { sx: { minHeight: '100%', display: 'grid', gridTemplateRows: '1fr auto', gridTemplateColumns: '100%' } },
            React.createElement(Box, null, props.children),
            React.createElement(Box, { sx: { alignSelf: 'end' } },
                React.createElement(GlobalContentFooter, Object.assign({}, props))))))));
};
